import React, { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import FlyerImg from '../../images/FlyerRight.png';
import jsPDF from 'jspdf';
import CardBack from "../Summary/CardBack";
import CardFront from "../Summary/CardFront";

// ─── Placeholder pixel coordinates in the native FlyerRight.png image ───
const FLYER_W = 2550;
const FLYER_H = 3300;
const PH = { x1: 60, y1: 2535, x2: 2490, y2: 3295 }; // card placeholder coords

const FlyerPage2 = forwardRef(function FlyerPage2({ bin, pcn, classnames, groupNumber, mId }, ref) {
    const containerRef = useRef(null);
    const imgRef       = useRef(null);
    const [cardStyle, setCardStyle] = useState(null);

    const handleFlyerLoad = useCallback(() => {
        const img = imgRef.current;
        if (!img || !img.clientWidth) return;
        const sx = img.clientWidth  / FLYER_W;
        const sy = img.clientHeight / FLYER_H;
        setCardStyle({
            position: 'absolute',
            left:   Math.round(PH.x1 * sx) + 'px',
            top:    Math.round(PH.y1 * sy) + 'px',
            width:  Math.round((PH.x2 - PH.x1) * sx) + 'px',
            height: Math.round((PH.y2 - PH.y1) * sy) + 'px',
        });
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => handleFlyerLoad());
        ro.observe(container);
        return () => ro.disconnect();
    }, [handleFlyerLoad]);

    // Helper: ensure an img element is fully loaded
    const waitForImg = (imgEl) => new Promise(res => {
        if (!imgEl) { res(); return; }
        if (imgEl.complete && imgEl.naturalWidth > 0) { res(); return; }
        imgEl.onload  = res;
        imgEl.onerror = res;
    });

    // Build composite canvas: FlyerRight.png base + cards drawn at placeholder coords
    const composeCanvas = async () => {
        const container = containerRef.current;
        if (!container) return null;

        const flyerImgEl  = imgRef.current;
        const frontCanvas = container.querySelector('canvas');
        const backImgEl   = container.querySelector('.flyer-cards-area img') ||
                            container.querySelector('img[alt="Prescription Savings Card — Back"]');

        await waitForImg(flyerImgEl);
        await waitForImg(backImgEl);

        const scale = 2;
        const composite = document.createElement('canvas');
        composite.width  = FLYER_W * scale;
        composite.height = FLYER_H * scale;
        const ctx = composite.getContext('2d');
        ctx.scale(scale, scale);

        // Helper: draw image/canvas clipped to a rounded rectangle
        const drawRounded = (src, x, y, w, h, r) => {
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.lineTo(x + w - r, y);
            ctx.quadraticCurveTo(x + w, y,     x + w, y + r);
            ctx.lineTo(x + w, y + h - r);
            ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
            ctx.lineTo(x + r, y + h);
            ctx.quadraticCurveTo(x, y + h,     x, y + h - r);
            ctx.lineTo(x, y + r);
            ctx.quadraticCurveTo(x, y,         x + r, y);
            ctx.closePath();
            ctx.clip();
            ctx.drawImage(src, x, y, w, h);
            ctx.restore();
        };

        // 1. Draw full flyer image as base
        if (flyerImgEl) ctx.drawImage(flyerImgEl, 0, 0, FLYER_W, FLYER_H);

        // 2. Compute card draw area in native pixels (with tuned offsets for flyer2)
        const phW      = PH.x2 - PH.x1;
        const phH      = PH.y2 - PH.y1;
        const S        = FLYER_W / 700;
        const gapPx    = Math.round(16 * S);
        const cardW    = Math.round((phW - gapPx) / 2) - Math.round(15 * S);
        const cardH    = Math.round(cardW * (454 / 772));
        const shiftUp  = Math.round(18 * S);
        const shiftR   = Math.round(13 * S);
        const cardY    = PH.y1 + Math.round((phH - cardH) / 2) - shiftUp;
        const startX   = PH.x1 + shiftR;
        const radius   = Math.round(16 * S);

        // 3. Draw front card
        if (frontCanvas) {
            drawRounded(frontCanvas, startX, cardY, cardW, cardH, radius);
        }

        // 4. Draw back card
        if (backImgEl) {
            try { drawRounded(backImgEl, startX + cardW + gapPx, cardY, cardW, cardH, radius); }
            catch (e) { /* skip cross-origin */ }
        }

        return composite;
    };

    const downloaddoc = async () => {
        const composite = await composeCanvas();
        if (!composite) return;
        const pdf  = new jsPDF('p', 'mm', 'a4');
        const pdfW = pdf.internal.pageSize.getWidth();
        const pdfH = pdf.internal.pageSize.getHeight();
        const ratio = Math.min((pdfW - 10) / (composite.width / 2), (pdfH - 10) / (composite.height / 2));
        const w = (composite.width  / 2) * ratio;
        const h = (composite.height / 2) * ratio;
        // flyer2 PDF: slight right shift and position from top
        pdf.addImage(composite.toDataURL('image/png'), 'PNG', (pdfW - w) / 2 + 5, 10, w, h);
        pdf.save('flyer-2.pdf');
    };

    const printIndivisual = async () => {
        const composite = await composeCanvas();
        if (!composite) return;
        const imgSrc = composite.toDataURL('image/png');
        document.body.innerHTML = `<div style="text-align:center;padding:20px;"><img src="${imgSrc}" style="max-width:700px;width:100%;height:auto;display:inline-block;" /></div>`;
        setTimeout(() => { window.print(); window.location.reload(); }, 500);
    };

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
        composeCanvas,
        downloaddoc,
        printIndivisual,
    }));

    return (
        <div ref={containerRef} style={{ position: 'relative', lineHeight: 0 }}>
            {/* Download / Print buttons */}
            <div className="buttons">
                <div className="download" onClick={downloaddoc}></div>
                <div className="print"    onClick={printIndivisual}></div>
            </div>

            {/* Full flyer image as base */}
            <img
                ref={imgRef}
                src={FlyerImg}
                alt="Flyer 2"
                onLoad={handleFlyerLoad}
                style={{ width: '100%', display: 'block', height: 'auto' }}
            />

            {/* Cards overlaid on the placeholder rectangle */}
            {cardStyle && (
                <div
                    className="flyer-cards-area"
                    style={{
                        ...cardStyle,
                        display:    'flex',
                        gap:        '16px',
                        alignItems: 'center',
                        boxSizing:  'border-box',
                        background: 'transparent',
                    }}
                >
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <CardFront
                            bin={bin}
                            pcn={pcn}
                            cardData={{ classnames }}
                            PrimaryGn={groupNumber}
                            mId={mId}
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <CardBack cardData={{ classnames }} />
                    </div>
                </div>
            )}
        </div>
    );
});

export default FlyerPage2;
